import React, { useState } from 'react';
import { MessageSquare, Tag, FileText, Briefcase, HelpCircle, Type, Send, Copy, Check, Loader } from 'lucide-react';
import { aiChat, generateMetaTags, optimizeContent, generateContentBrief, generateFAQ, generateHeadlines } from '../services/aiService';
import { t } from '../services/i18n';

type ToolType = 'chat' | 'meta' | 'optimize' | 'brief' | 'faq' | 'headlines';

export const AITools: React.FC = () => {
  // All hooks must be at the top level (not inside try-catch)
  const [activeTool, setActiveTool] = useState<ToolType>('chat');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Chat state
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [siteContext, setSiteContext] = useState<any>(null);

  // Meta Tag state
  const [metaPageUrl, setMetaPageUrl] = useState('');
  const [metaContent, setMetaContent] = useState('');
  const [metaKeyword, setMetaKeyword] = useState('');
  const [metaResult, setMetaResult] = useState<any>(null);

  // Content Optimizer state
  const [contentToOptimize, setContentToOptimize] = useState('');
  const [optimizeKeyword, setOptimizeKeyword] = useState('');
  const [optimizeCompetitors, setOptimizeCompetitors] = useState('');
  const [optimizeResult, setOptimizeResult] = useState<any>(null);

  // Content Brief state
  const [briefKeyword, setBriefKeyword] = useState('');
  const [briefCompetitors, setBriefCompetitors] = useState('');
  const [briefResult, setBriefResult] = useState<any>(null);

  // FAQ state
  const [faqTopic, setFaqTopic] = useState('');
  const [faqAudience, setFaqAudience] = useState('');
  const [faqResult, setFaqResult] = useState<any>(null);

  // Headlines state
  const [headlineTopic, setHeadlineTopic] = useState('');
  const [headlineKeyword, setHeadlineKeyword] = useState('');
  const [headlineStyle, setHeadlineStyle] = useState<'listicle' | 'how-to' | 'guide' | 'comparison' | 'review'>('guide');
  const [headlineResult, setHeadlineResult] = useState<any>(null);

  const [copied, setCopied] = useState<string | null>(null);

  // Safe translation helper
  const safeT = (key: string, fallback: string): string => {
    try {
      const translated = t(key);
      return translated && translated !== key ? translated : fallback;
    } catch {
      return fallback;
    }
  };

  const tools = [
    { id: 'chat' as ToolType, label: safeT('aiTools.chat.title', 'AI SEO Chat'), icon: <MessageSquare size={20} />, description: safeT('aiTools.chat.description', 'Get personalized SEO advice') },
    { id: 'meta' as ToolType, label: safeT('aiTools.meta.title', 'Meta Tag Generator'), icon: <Tag size={20} />, description: safeT('aiTools.meta.description', 'Generate optimized meta tags') },
    { id: 'optimize' as ToolType, label: safeT('aiTools.optimize.title', 'Content Optimizer'), icon: <FileText size={20} />, description: safeT('aiTools.optimize.description', 'Analyze and optimize content') },
    { id: 'brief' as ToolType, label: safeT('aiTools.brief.title', 'Content Brief'), icon: <Briefcase size={20} />, description: safeT('aiTools.brief.description', 'Generate content outlines') },
    { id: 'faq' as ToolType, label: safeT('aiTools.faq.title', 'FAQ Generator'), icon: <HelpCircle size={20} />, description: safeT('aiTools.faq.description', 'Create FAQ sections') },
    { id: 'headlines' as ToolType, label: safeT('aiTools.headlines.title', 'Headline Generator'), icon: <Type size={20} />, description: safeT('aiTools.headlines.description', 'Generate A/B test headlines') },
  ];

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  // Chat Handler
  const handleChat = async () => {
    if (!chatMessage.trim()) return;
    
    setLoading(true);
    setError(null);
    const userMessage = chatMessage;
    setChatMessage('');
    
    // Add user message to history
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    
    // Show loading message if website URL is provided (audit in progress)
    if (websiteUrl && chatHistory.length === 0) {
      setChatHistory(prev => [...prev, { 
        role: 'assistant', 
        content: '🔍 Analyzing your website SEO first... This will help me give you better advice!' 
      }]);
    }

    try {
      // If website URL is provided and this is the first message, the backend will auto-analyze
      const response = await aiChat(userMessage, websiteUrl || undefined, siteContext || undefined);
      
      // Remove the loading message if it exists
      if (websiteUrl && chatHistory.length > 0 && chatHistory[chatHistory.length - 1].content.includes('Analyzing')) {
        setChatHistory(prev => prev.slice(0, -1));
      }
      
      setChatHistory(prev => [...prev, { role: 'assistant', content: response.response }]);
    } catch (err: any) {
      // Remove loading message on error
      if (websiteUrl && chatHistory.length > 0 && chatHistory[chatHistory.length - 1].content.includes('Analyzing')) {
        setChatHistory(prev => prev.slice(0, -1));
      }
      setError(err.message || 'Failed to get response');
      setChatHistory(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  // Meta Tag Handler
  const handleGenerateMeta = async () => {
    if (!metaContent.trim() || !metaKeyword.trim()) {
      setError('Content and keyword are required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await generateMetaTags(metaPageUrl, metaContent, metaKeyword);
      setMetaResult(result);
    } catch (err: any) {
      setError(err.message || 'Failed to generate meta tags');
    } finally {
      setLoading(false);
    }
  };

  // Content Optimizer Handler
  const handleOptimize = async () => {
    if (!contentToOptimize.trim() || !optimizeKeyword.trim()) {
      setError('Content and keyword are required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const competitors = optimizeCompetitors.split(',').map(c => c.trim()).filter(c => c);
      const result = await optimizeContent(contentToOptimize, optimizeKeyword, competitors.length > 0 ? competitors : undefined);
      setOptimizeResult(result);
    } catch (err: any) {
      setError(err.message || 'Failed to optimize content');
    } finally {
      setLoading(false);
    }
  };

  // Content Brief Handler
  const handleGenerateBrief = async () => {
    if (!briefKeyword.trim()) {
      setError('Target keyword is required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const competitors = briefCompetitors.split(',').map(c => c.trim()).filter(c => c);
      const result = await generateContentBrief(briefKeyword, competitors.length > 0 ? competitors : undefined);
      setBriefResult(result);
    } catch (err: any) {
      setError(err.message || 'Failed to generate content brief');
    } finally {
      setLoading(false);
    }
  };

  // FAQ Handler
  const handleGenerateFAQ = async () => {
    if (!faqTopic.trim()) {
      setError('Topic is required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await generateFAQ(faqTopic, faqAudience || undefined);
      setFaqResult(result);
    } catch (err: any) {
      setError(err.message || 'Failed to generate FAQ');
    } finally {
      setLoading(false);
    }
  };

  // Headlines Handler
  const handleGenerateHeadlines = async () => {
    if (!headlineTopic.trim() || !headlineKeyword.trim()) {
      setError('Topic and keyword are required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await generateHeadlines(headlineTopic, headlineKeyword, headlineStyle);
      setHeadlineResult(result);
    } catch (err: any) {
      setError(err.message || 'Failed to generate headlines');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 animate-fadeIn text-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">{safeT('aiTools.title', 'AI-Powered SEO Tools')}</h2>
          <p className="text-slate-400">{safeT('aiTools.subtitle', 'Advanced AI tools to optimize your SEO and content')}</p>
        </div>

        {/* Tool Selector */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          {tools.map((tool) => (
            <button
              key={tool.id}
              onClick={() => setActiveTool(tool.id)}
              className={`p-4 rounded-xl border transition-all ${
                activeTool === tool.id
                  ? 'bg-indigo-600 border-indigo-500 text-white'
                  : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-indigo-500/50'
              }`}
            >
              <div className="flex flex-col items-center gap-2">
                {tool.icon}
                <div className="text-xs font-semibold text-center">{tool.label}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {/* Tool Content */}
        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
          {/* AI SEO Chat */}
          {activeTool === 'chat' && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold mb-4">{safeT('aiTools.chat.title', 'AI SEO Chat Assistant')}</h3>
              
              {/* Context Input */}
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Website URL (optional)</label>
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                />
              </div>

              {/* Example Prompts */}
              {chatHistory.length === 0 && (
                <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-semibold text-slate-300 mb-3">💡 Example Prompts:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {[
                      'How can I improve my SEO score?',
                      'What are the most critical SEO issues on my site?',
                      'Explain meta tags in simple terms',
                      'How do I optimize images for SEO?',
                      'What is the best keyword strategy?',
                      'How to improve page loading speed?'
                    ].map((prompt, idx) => (
                      <button
                        key={idx}
                        onClick={() => setChatMessage(prompt)}
                        className="text-left p-2 bg-slate-800 hover:bg-slate-700 rounded text-xs text-slate-300 hover:text-white transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                  {websiteUrl && (
                    <div className="mt-3 p-2 bg-indigo-500/10 border border-indigo-500/30 rounded text-xs text-indigo-300">
                      ℹ️ Enter a website URL above to automatically analyze your site's SEO before chatting
                    </div>
                  )}
                </div>
              )}

              {/* Chat History */}
              <div className="bg-slate-900/50 rounded-lg p-4 h-96 overflow-y-auto space-y-4">
                {chatHistory.length === 0 ? (
                  <div className="text-center text-slate-500 py-8">
                    <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
                    <p>{safeT('aiTools.chat.startMessage', 'Start a conversation about SEO...')}</p>
                    {websiteUrl && (
                      <p className="text-xs mt-2 text-indigo-400">
                        Your website will be analyzed automatically when you send your first message
                      </p>
                    )}
                  </div>
                ) : (
                  chatHistory.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-lg ${
                          msg.role === 'user'
                            ? 'bg-indigo-600 text-white'
                            : 'bg-slate-700 text-slate-200'
                        }`}
                      >
                        <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                      </div>
                    </div>
                  ))
                )}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-700 p-3 rounded-lg">
                      <Loader className="animate-spin" size={20} />
                    </div>
                  </div>
                )}
              </div>

              {/* Chat Input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleChat()}
                  placeholder={safeT('aiTools.chat.placeholder', 'Ask about SEO...')}
                  className="flex-1 bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  disabled={loading}
                />
                <button
                  onClick={handleChat}
                  disabled={loading || !chatMessage.trim()}
                  className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Meta Tag Generator */}
          {activeTool === 'meta' && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold mb-4">{safeT('aiTools.meta.title', 'Meta Tag Generator')}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Page URL (optional)</label>
                  <input
                    type="url"
                    value={metaPageUrl}
                    onChange={(e) => setMetaPageUrl(e.target.value)}
                    placeholder="https://example.com/page"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Page Content *</label>
                  <textarea
                    value={metaContent}
                    onChange={(e) => setMetaContent(e.target.value)}
                    placeholder="Paste your page content here..."
                    rows={6}
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Target Keyword *</label>
                  <input
                    type="text"
                    value={metaKeyword}
                    onChange={(e) => setMetaKeyword(e.target.value)}
                    placeholder="e.g., best SEO tools"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  />
                </div>
                <button
                  onClick={handleGenerateMeta}
                  disabled={loading}
                  className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader className="animate-spin" /> : <Tag size={18} />}
                  {safeT('aiTools.meta.generate', 'Generate Meta Tags')}
                </button>

                {metaResult && (
                  <div className="mt-6 space-y-4 p-4 bg-slate-900/50 rounded-lg">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-semibold text-slate-300">Title</label>
                        <button
                          onClick={() => handleCopy(metaResult.title, 'meta-title')}
                          className="text-indigo-400 hover:text-indigo-300"
                        >
                          {copied === 'meta-title' ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                      </div>
                      <div className="bg-slate-800 p-3 rounded text-sm text-white font-mono">
                        {metaResult.title}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        Length: {metaResult.title_length} characters {metaResult.title_length <= 60 ? '✅' : '⚠️'}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-semibold text-slate-300">Description</label>
                        <button
                          onClick={() => handleCopy(metaResult.description, 'meta-desc')}
                          className="text-indigo-400 hover:text-indigo-300"
                        >
                          {copied === 'meta-desc' ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                      </div>
                      <div className="bg-slate-800 p-3 rounded text-sm text-white font-mono">
                        {metaResult.description}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        Length: {metaResult.description_length} characters {metaResult.description_length <= 160 ? '✅' : '⚠️'}
                      </div>
                    </div>
                    {metaResult.reasoning && (
                      <div className="text-sm text-slate-300 italic">
                        💡 {metaResult.reasoning}
                      </div>
                    )}
                    {metaResult.variations && metaResult.variations.length > 0 && (
                      <div className="mt-4">
                        <label className="text-sm font-semibold text-slate-300 mb-2 block">Alternative Variations</label>
                        {metaResult.variations.map((variation: any, idx: number) => (
                          <div key={idx} className="mb-3 p-3 bg-slate-800 rounded">
                            <div className="text-xs text-slate-400 mb-1">Title: {variation.title}</div>
                            <div className="text-xs text-slate-400">Description: {variation.description}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content Optimizer */}
          {activeTool === 'optimize' && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold mb-4">{safeT('aiTools.optimize.title', 'Content Optimizer & Scorer')}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Content to Analyze *</label>
                  <textarea
                    value={contentToOptimize}
                    onChange={(e) => setContentToOptimize(e.target.value)}
                    placeholder="Paste your content here..."
                    rows={10}
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Target Keyword *</label>
                  <input
                    type="text"
                    value={optimizeKeyword}
                    onChange={(e) => setOptimizeKeyword(e.target.value)}
                    placeholder="e.g., best SEO tools"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Competitor URLs (comma-separated, optional)</label>
                  <input
                    type="text"
                    value={optimizeCompetitors}
                    onChange={(e) => setOptimizeCompetitors(e.target.value)}
                    placeholder="https://competitor1.com, https://competitor2.com"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  />
                </div>
                <button
                  onClick={handleOptimize}
                  disabled={loading}
                  className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader className="animate-spin" /> : <FileText size={18} />}
                  {safeT('aiTools.optimize.analyze', 'Analyze & Optimize (2 Credits)')}
                </button>

                {optimizeResult && (
                  <div className="mt-6 space-y-4">
                    {/* Scores */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-slate-900/50 p-4 rounded-lg">
                        <div className="text-xs text-slate-400 mb-1">SEO Score</div>
                        <div className="text-2xl font-bold text-indigo-400">{optimizeResult.seo_score || 0}</div>
                      </div>
                      <div className="bg-slate-900/50 p-4 rounded-lg">
                        <div className="text-xs text-slate-400 mb-1">Keyword Score</div>
                        <div className="text-2xl font-bold text-blue-400">{optimizeResult.keyword_score || 0}</div>
                      </div>
                      <div className="bg-slate-900/50 p-4 rounded-lg">
                        <div className="text-xs text-slate-400 mb-1">Structure Score</div>
                        <div className="text-2xl font-bold text-purple-400">{optimizeResult.structure_score || 0}</div>
                      </div>
                      <div className="bg-slate-900/50 p-4 rounded-lg">
                        <div className="text-xs text-slate-400 mb-1">Readability</div>
                        <div className="text-2xl font-bold text-green-400">{optimizeResult.readability_score || 0}</div>
                      </div>
                    </div>

                    {/* Issues */}
                    {optimizeResult.issues && optimizeResult.issues.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold mb-3">Issues Found</h4>
                        <div className="space-y-3">
                          {optimizeResult.issues.map((issue: any, idx: number) => (
                            <div key={idx} className="bg-slate-900/50 p-4 rounded-lg border-l-4 border-orange-500">
                              <div className="flex items-center justify-between mb-2">
                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                  issue.severity === 'Critical' ? 'bg-red-500/20 text-red-400' :
                                  issue.severity === 'High' ? 'bg-orange-500/20 text-orange-400' :
                                  issue.severity === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                  'bg-blue-500/20 text-blue-400'
                                }`}>
                                  {issue.severity}
                                </span>
                                <span className="text-xs text-slate-400">{issue.type}</span>
                              </div>
                              <div className="text-sm text-white font-semibold mb-1">{issue.issue}</div>
                              <div className="text-sm text-slate-300 mb-2">Fix: {issue.fix}</div>
                              <div className="text-xs text-slate-400">Impact: {issue.impact}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Improvements */}
                    {optimizeResult.improvements && optimizeResult.improvements.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold mb-3">Recommended Improvements</h4>
                        <ul className="space-y-2">
                          {optimizeResult.improvements.map((improvement: string, idx: number) => (
                            <li key={idx} className="flex items-start gap-2 text-slate-300">
                              <span className="text-indigo-400 mt-1">•</span>
                              <span>{improvement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Keyword Analysis */}
                    {optimizeResult.keyword_analysis && (
                      <div className="bg-slate-900/50 p-4 rounded-lg">
                        <h4 className="text-lg font-semibold mb-3">Keyword Analysis</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-slate-400">Count: </span>
                            <span className="text-white">{optimizeResult.keyword_analysis.count}</span>
                          </div>
                          <div>
                            <span className="text-slate-400">Density: </span>
                            <span className="text-white">{optimizeResult.keyword_analysis.density}%</span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-slate-400">Recommendations: </span>
                            <span className="text-white">{optimizeResult.keyword_analysis.recommendations}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content Brief Generator */}
          {activeTool === 'brief' && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold mb-4">{safeT('aiTools.brief.title', 'Content Brief Generator')}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Target Keyword *</label>
                  <input
                    type="text"
                    value={briefKeyword}
                    onChange={(e) => setBriefKeyword(e.target.value)}
                    placeholder="e.g., how to do keyword research"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Competitor URLs (comma-separated, optional)</label>
                  <input
                    type="text"
                    value={briefCompetitors}
                    onChange={(e) => setBriefCompetitors(e.target.value)}
                    placeholder="https://competitor1.com, https://competitor2.com"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  />
                </div>
                <button
                  onClick={handleGenerateBrief}
                  disabled={loading}
                  className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader className="animate-spin" /> : <Briefcase size={18} />}
                  {safeT('aiTools.brief.generate', 'Generate Content Brief')}
                </button>

                {briefResult && (
                  <div className="mt-6 space-y-4 p-4 bg-slate-900/50 rounded-lg">
                    {briefResult.titles && briefResult.titles.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold mb-3">Recommended Titles</h4>
                        <div className="space-y-2">
                          {briefResult.titles.map((title: string, idx: number) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-slate-800 rounded">
                              <span className="text-white">{title}</span>
                              <button
                                onClick={() => handleCopy(title, `brief-title-${idx}`)}
                                className="text-indigo-400 hover:text-indigo-300"
                              >
                                {copied === `brief-title-${idx}` ? <Check size={16} /> : <Copy size={16} />}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {briefResult.structure && briefResult.structure.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold mb-3">Article Structure</h4>
                        <div className="space-y-2">
                          {briefResult.structure.map((section: any, idx: number) => (
                            <div key={idx} className="p-3 bg-slate-800 rounded">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-bold text-indigo-400 uppercase">{section.type}</span>
                                <span className="text-sm font-semibold text-white">{section.title}</span>
                                <span className="text-xs text-slate-400">({section.word_count} words)</span>
                              </div>
                              {section.content_points && (
                                <ul className="ml-4 mt-2 space-y-1">
                                  {section.content_points.map((point: string, pIdx: number) => (
                                    <li key={pIdx} className="text-sm text-slate-300">• {point}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {briefResult.questions_to_answer && briefResult.questions_to_answer.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold mb-3">Questions to Answer</h4>
                        <ul className="space-y-2">
                          {briefResult.questions_to_answer.map((q: string, idx: number) => (
                            <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                              <span className="text-indigo-400 mt-1">•</span>
                              <span>{q}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {briefResult.target_word_count && (
                      <div className="text-sm text-slate-300">
                        <span className="font-semibold">Target Word Count: </span>
                        {briefResult.target_word_count} words
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* FAQ Generator */}
          {activeTool === 'faq' && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold mb-4">{safeT('aiTools.faq.title', 'FAQ Generator')}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Topic *</label>
                  <input
                    type="text"
                    value={faqTopic}
                    onChange={(e) => setFaqTopic(e.target.value)}
                    placeholder="e.g., keyword research"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Target Audience (optional)</label>
                  <input
                    type="text"
                    value={faqAudience}
                    onChange={(e) => setFaqAudience(e.target.value)}
                    placeholder="e.g., beginners, small business owners"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  />
                </div>
                <button
                  onClick={handleGenerateFAQ}
                  disabled={loading}
                  className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader className="animate-spin" /> : <HelpCircle size={18} />}
                  {safeT('aiTools.faq.generate', 'Generate FAQ')}
                </button>

                {faqResult && faqResult.faqs && (
                  <div className="mt-6 space-y-4">
                    <div className="space-y-4">
                      {faqResult.faqs.map((faq: any, idx: number) => (
                        <div key={idx} className="bg-slate-900/50 p-4 rounded-lg">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="text-white font-semibold">{faq.question}</h4>
                            <button
                              onClick={() => handleCopy(`${faq.question}\n${faq.answer}`, `faq-${idx}`)}
                              className="text-indigo-400 hover:text-indigo-300"
                            >
                              {copied === `faq-${idx}` ? <Check size={16} /> : <Copy size={16} />}
                            </button>
                          </div>
                          <p className="text-slate-300 text-sm">{faq.answer}</p>
                          <div className="mt-2 flex gap-2">
                            <span className="text-xs text-slate-400">Length: {faq.answer_length} chars</span>
                            <span className="text-xs text-slate-400">•</span>
                            <span className="text-xs text-slate-400">Intent: {faq.search_intent}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {faqResult.schema_markup && (
                      <div className="mt-4 p-4 bg-slate-900/50 rounded-lg">
                        <h4 className="text-sm font-semibold text-slate-300 mb-2">Schema Markup (JSON-LD)</h4>
                        <pre className="text-xs text-slate-400 overflow-x-auto">
                          {JSON.stringify(faqResult.schema_markup, null, 2)}
                        </pre>
                        <button
                          onClick={() => handleCopy(JSON.stringify(faqResult.schema_markup, null, 2), 'faq-schema')}
                          className="mt-2 text-indigo-400 hover:text-indigo-300 text-sm"
                        >
                          {copied === 'faq-schema' ? <Check size={16} className="inline" /> : <Copy size={16} className="inline" />} Copy Schema
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Headline Generator */}
          {activeTool === 'headlines' && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold mb-4">{safeT('aiTools.headlines.title', 'Headline Generator')}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Topic *</label>
                  <input
                    type="text"
                    value={headlineTopic}
                    onChange={(e) => setHeadlineTopic(e.target.value)}
                    placeholder="e.g., SEO checklist"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Keyword *</label>
                  <input
                    type="text"
                    value={headlineKeyword}
                    onChange={(e) => setHeadlineKeyword(e.target.value)}
                    placeholder="e.g., SEO checklist"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Style</label>
                  <select
                    value={headlineStyle}
                    onChange={(e) => setHeadlineStyle(e.target.value as any)}
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  >
                    <option value="guide">Guide</option>
                    <option value="listicle">Listicle</option>
                    <option value="how-to">How-To</option>
                    <option value="comparison">Comparison</option>
                    <option value="review">Review</option>
                  </select>
                </div>
                <button
                  onClick={handleGenerateHeadlines}
                  disabled={loading}
                  className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader className="animate-spin" /> : <Type size={18} />}
                  {safeT('aiTools.headlines.generate', 'Generate Headlines')}
                </button>

                {headlineResult && headlineResult.headlines && (
                  <div className="mt-6 space-y-4">
                    {headlineResult.recommended && (
                      <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
                        <div className="text-xs text-indigo-400 uppercase font-bold mb-1">Recommended</div>
                        <div className="text-lg font-bold text-white mb-1">{headlineResult.recommended.headline}</div>
                        <div className="text-sm text-slate-300">{headlineResult.recommended.reason}</div>
                      </div>
                    )}
                    <div className="space-y-3">
                      {headlineResult.headlines.map((headline: any, idx: number) => (
                        <div key={idx} className="bg-slate-900/50 p-4 rounded-lg">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="text-white font-semibold mb-1">{headline.headline}</div>
                              <div className="flex gap-3 text-xs text-slate-400">
                                <span>Length: {headline.length} chars</span>
                                <span>•</span>
                                <span>Format: {headline.format}</span>
                                <span>•</span>
                                <span className={`${
                                  headline.ctr_estimate === 'high' ? 'text-green-400' :
                                  headline.ctr_estimate === 'medium' ? 'text-yellow-400' :
                                  'text-slate-400'
                                }`}>
                                  CTR: {headline.ctr_estimate}
                                </span>
                              </div>
                              {headline.reasoning && (
                                <div className="text-xs text-slate-400 mt-2 italic">{headline.reasoning}</div>
                              )}
                            </div>
                            <button
                              onClick={() => handleCopy(headline.headline, `headline-${idx}`)}
                              className="text-indigo-400 hover:text-indigo-300 ml-2"
                            >
                              {copied === `headline-${idx}` ? <Check size={16} /> : <Copy size={16} />}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

